import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../apiConfig";
import "../Styles/userAttendance.css";
import { CalendarClock } from "lucide-react";
import Header from "../Components/Common/Header";
import Pagination from "../Components/Common/Pagination";

const UserAttendance = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("recent"); 
  const [selectedDate, setSelectedDate] = useState("");
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: ""
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const fetchAttendance = useCallback(async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    
    try {
      
      const response = await fetch(
        `${API_BASE_URL}/attendance/continuous-history?startDate=${startDate}&endDate=${endDate}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        }
      );
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      const data = await response.json();
      if (data.success) {
        setAttendanceRecords(data.attendance);
        updateFilteredRecords(data.attendance, 1); 
      } else {
        setError(data.message || "Failed to fetch attendance records");
        console.error("Failed to fetch attendance records:", data.message);
      }
    } catch (err) {
      setError("Error connecting to server. Please try again later.");
      console.error("Error fetching attendance records:", err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsPerPage]);

  useEffect(() => {
  
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6); 
    
    setDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
    
    fetchAttendance(start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
  }, [fetchAttendance]);


  const updateFilteredRecords = (records, page) => {
    const indexOfLastRecord = page * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    setFilteredRecords(records.slice(indexOfFirstRecord, indexOfLastRecord));
    setCurrentPage(page);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === "recent") {
   
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      
      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];
      
      setDateRange({
        startDate: startDateStr,
        endDate: endDateStr
      });
      
      fetchAttendance(startDateStr, endDateStr);
      setSelectedDate("");
    }
  };

  const handleDateChange = (event) => {
    const date = event.target.value;
    setSelectedDate(date);
    
    if (date) {
     
      setDateRange({
        startDate: date,
        endDate: date
      });
      
      fetchAttendance(date, date);
    }
  };


  const paginate = (pageNumber) => {
    if (mode === "date") return; 
    updateFilteredRecords(attendanceRecords, pageNumber);
  };


  const totalPages = Math.ceil(attendanceRecords.length / recordsPerPage);

  const getStatusClass = (status) => {
    status = status.toLowerCase();
    if (status.includes("present")) return "status-present";
    if (status.includes("absent")) return "status-absent";
    if (status.includes("late")) return "status-late";
    return "";
  };

  return (
    <div className="user-attendance-page">
      <div className="attendance-content">
        <Header 
          title="Attendance Records" 
          description="View and track your attendance history" 
          icon={<CalendarClock size={28} color="#f3f4f6" />}
        />

        <div className="attendance-card">
          <div className="attendance-card-header">
            <h2>Attendance Records</h2>
            
            <div className="attendance-filters">
              <div className="mode-toggle">
                <button
                  className={`toggle-button ${mode === "recent" ? "active" : ""}`}
                  onClick={() => handleModeChange("recent")}
                >
                  Recent Days
                </button>
                <button
                  className={`toggle-button ${mode === "date" ? "active" : ""}`}
                  onClick={() => handleModeChange("date")}
                >
                  Pick by Date
                </button>
              </div>

              {mode === "date" && (
                <div className="date-picker">
                  <label htmlFor="date">Select Date:</label>
                  <input
                    type="date"
                    id="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                  />
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : error ? (
            <div className="empty-state">
              <p>{error}</p>
              <button 
                className="refresh-button" 
                onClick={() => fetchAttendance(dateRange.startDate, dateRange.endDate)}
              >
                Try Again
              </button>
            </div>
          ) : filteredRecords.length > 0 ? (
            <div className="attendance-table-container">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Check-In</th>
                    <th>Check-Out</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record, index) => (
                    <tr key={index}>
                      <td>{record.date}</td>
                      <td className="time-cell">{record.checkInTime || "Not Recorded"}</td>
                      <td className="time-cell">{record.checkOutTime || "Not Recorded"}</td>
                      <td>
                        <span className={`status-pill ${getStatusClass(record.status)}`}>
                          {record.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={paginate}
                />
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>No attendance records found for the selected criteria.</p>
              <button 
                className="refresh-button" 
                onClick={() => fetchAttendance(dateRange.startDate, dateRange.endDate)}
              >
                Refresh Data
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAttendance;
